import AddFolderBtn from '@Resources/assets/icons/dark/addFolder.svg';
import UploadDataBtn from '@Resources/assets/icons/dark/cloud2.svg';
import NotVisibleIcon from '@Resources/assets/icons/dark/notVisible.svg';
import Flex from '@Resources/styles/flex.scss';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import {
    Divider,
    Menu,
    MenuItem,
    Skeleton,
    SvgIcon,
    Tooltip,
} from '@mui/material';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FolderOption } from '@Components/FolderComponents/FolderOption';
import FolderOptionSkeleton from '@Components/FolderComponents/FolderOptionSkeleton';
import IconButton from '@Components/IconButton/IconButton';
import IconBtnStyles from '@Components/IconButton/IconButton.scss';
import DownloadReportsDialogue from '@Components/ModalInterfaces/DownloadReports/DownloadReportsDialogue';
import JobSettingsDialog from '@Components/ModalInterfaces/JobSettings/JobSettingsDialog';
import { SettingsType } from '@Components/Settings/SettingsType';
import DepartmentsApi from '@Data/departments/Api';
import FilesApi from '@Data/files/Api';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import {
    foldersState,
    selectedFolderIdState,
    visibleFoldersState,
} from '@Data/folders/Reducer';
import ProcessesApi from '@Data/processes/Api';
import { linkDepartmentProcessState } from '@Data/processes/Reducer';
import { IProject } from '@Data/projects/Interfaces';
import { ProjectActionType } from '@Data/projects/ProjectActionTypes';
import { SettingsApi } from '@Data/settings/Api';
import { ITeam } from '@Data/teams/Interfaces';
import { KoruRoutes } from '@Services/navigation/Paths';
import ProjectModalDialog from '../../ProjectModalDialogue/ProjectModalDialogue';
import Styles from './ContentsDrawer.scss';
import ManageFolderDialogue from './ManageFolderDialogue';
import ProjectFolderMenu from './ProjectFolderMenu';

interface IContentsDrawerProps {
    isLoading: boolean;
    selectedProject: IProject;
    selectedTeam: ITeam;
    onManageProject: () => void;
}

const ContentsDrawer = (props: IContentsDrawerProps) => {
    const { isLoading, selectedProject, onManageProject, selectedTeam } = props;

    const params = useParams();
    const organizationId = params.organizationId;
    const teamId = params.teamId;
    const projectId = params.projectId;
    const visibleFolderIds = params.folderIds;

    const navigate = useNavigate();

    const folders: Array<IFolder> = useSelector(foldersState);
    const linkDepartmentProcess = useSelector(linkDepartmentProcessState);
    const visibleFolders: Array<IFolder> = useSelector(visibleFoldersState);
    const selectedFolderId: string = useSelector(selectedFolderIdState);
    const [isManageDialogueOpen, setIsManageDialogueOpen] =
        useState<boolean>(false);
    const [isDownloadFilesOpen, setIsDownloadFilesOpen] =
        useState<boolean>(false);
    const [openDialogueType, setOpenDialogueType] =
        useState<ProjectActionType | null>(null);
    const [selectedFolder, setSelectedFolder] = useState<IFolder>(null);
    const [dataMenuIsOpen, setDataMenIsOpen] = useState<boolean>(false);
    const [isJobSettingsOpen, setIsJobSettingsOpen] = useState<boolean>(false);
    const uploadDataBtnId = 'upload-data-btn';
    const projectSettingsBtnId = 'project-settings-btn';

    useEffect(() => {
        ProcessesApi.getProcesses();
    }, []);

    const handleCancelManage = () => {
        setIsManageDialogueOpen(false);
        setSelectedFolder(null);
    };

    const handleSubmitManage = (folderName: string, manageFolder: IFolder) => {
        const cb = () => {
            setIsManageDialogueOpen(false);
            onManageProject();
        };

        if (!_isNil(manageFolder)) {
            FoldersApi.editFolderName(folderName, manageFolder, cb);
        } else {
            FoldersApi.createFolder(folderName, selectedProject.project_id, cb);
        }
    };

    const handleFileUploadBtnClick = () => {
        setOpenDialogueType(ProjectActionType.UPLOAD_FILE);
    };

    const handleConnectDeptClick = () => {
        setOpenDialogueType(ProjectActionType.CONNECT_DEPT);
    };

    const handleEditFolder = (selectedWp: IFolder) => {
        setSelectedFolder(selectedWp);
        setIsManageDialogueOpen(true);
    };

    const handleDialogueClose = () => {
        ProcessesApi.setProcess(null);
        DepartmentsApi.setDepartments(null);
        setSelectedFolder(null);
        setOpenDialogueType(null);
        setIsDownloadFilesOpen(false);
        setIsManageDialogueOpen(false);
    };

    const handleProcessFolder = (selectedWp: IFolder) => {
        setSelectedFolder(selectedWp);
        setOpenDialogueType(ProjectActionType.OPEN_PROCESS);
    };

    const handleCreateSubset = (folder: IFolder) => {
        setSelectedFolder(folder);
        setOpenDialogueType(ProjectActionType.CREATE_SUBSET);
    };

    const handleShareFolder = (selectedWp: IFolder) => {
        setSelectedFolder(selectedWp);
        FilesApi.downloadProjectFile(selectedWp);
    };

    const handleCompleteProcessEvent = () => {
        handleDialogueClose();
    };

    const handleCreateFolderBtnClick = () => {
        setSelectedFolder(null);
        setIsManageDialogueOpen(true);
    };

    const handleZoomToBounds = (folder: IFolder) => {
        FoldersApi.zoomToBounds(
            folder.folder_id,
            visibleFolders.map((f) => f.folder_id)
        );
    };

    const handleViewFiles = (folder: IFolder) => {
        setSelectedFolder(folder);
        setIsDownloadFilesOpen(true);
    };

    const handleOpenJobSettings = (folder: IFolder) => {
        setSelectedFolder(folder);
        setIsJobSettingsOpen(true);
    };

    const onSaveJobSettings = async () => {
        await FoldersApi.getFolders(selectedProject.project_id);
        setIsJobSettingsOpen(false);
    };

    const handleToggleVisibility = (folder: IFolder) => {
        FoldersApi.toggleFolderVisibility(folder, () => {
            const existingFolderIds = _isNil(visibleFolderIds)
                ? []
                : visibleFolderIds.split(',').filter((id) => id !== '');
            const existingFolderIdIndex = existingFolderIds.findIndex(
                (id) => folder.folder_id === id
            );
            if (existingFolderIdIndex > -1) {
                existingFolderIds.splice(existingFolderIdIndex, 1);
            } else {
                existingFolderIds.push(folder.folder_id);
            }

            navigate(
                KoruRoutes.project.getUrl(
                    organizationId,
                    teamId,
                    projectId,
                    existingFolderIds
                )
            );
        });
    };

    const handleClearVisibleFolders = () => {
        FoldersApi.clearVisibleFolders();
    };

    const toggleDataMenu = () => {
        setDataMenIsOpen(!dataMenuIsOpen);
    };

    const handleOpenProjectSettingsDialog = () => {
        SettingsApi.openSettings(SettingsType.PROJECT);
    };

    const foldersHtml =
        isLoading || _isNil(selectedProject) ? (
            <div className={cx(Flex.flex, Flex.column)}>
                <FolderOptionSkeleton />
                <FolderOptionSkeleton />
                <FolderOptionSkeleton />
                <FolderOptionSkeleton />
                <FolderOptionSkeleton />
            </div>
        ) : (
            folders?.map((folder, i) => {
                const isVisible =
                    visibleFolders.findIndex(
                        (vfolder) => vfolder.folder_id === folder.folder_id
                    ) > -1;
                const isSelected = folder.folder_id === selectedFolderId;
                return (
                    <FolderOption
                        gridRef={null}
                        id={`folder-option-${i}`}
                        folder={folder}
                        linkDepartmentProcess={linkDepartmentProcess}
                        key={folder.folder_id}
                        onClick={() => handleToggleVisibility(folder)}
                        folderMenu={
                            <ProjectFolderMenu
                                id={`folder-option-${i}-menu`}
                                folder={folder}
                                isVisible={isVisible}
                                isSelected={isSelected}
                                onEdit={() => handleEditFolder(folder)}
                                onProcess={() => handleProcessFolder(folder)}
                                onShare={() => handleShareFolder(folder)}
                                onZoomToBounds={() =>
                                    handleZoomToBounds(folder)
                                }
                                onCreateSubset={() =>
                                    handleCreateSubset(folder)
                                }
                                onViewFiles={() => handleViewFiles(folder)}
                                onOpenJobSettings={() =>
                                    handleOpenJobSettings(folder)
                                }
                            />
                        }
                        columns={[]}
                        setColumns={function (GridColDef: any): void {
                            throw new Error('Function not implemented.');
                        }}
                    />
                );
            })
        );

    const createFolder = (
        <IconButton
            id="create-folder-btn"
            onClick={handleCreateFolderBtnClick}
            tooltip="Create Folder"
            icon={
                <SvgIcon inheritViewBox>
                    <AddFolderBtn />
                </SvgIcon>
            }
            key={'createFolderIconButton'}
        />
    );

    const hideFolder = (
        <IconButton
            id="hide-folder-btn"
            onClick={handleClearVisibleFolders}
            tooltip="Hide All"
            icon={
                <SvgIcon inheritViewBox>
                    <NotVisibleIcon />
                </SvgIcon>
            }
            key={'hideFolderIconButton'}
        />
    );

    const uploadData = (
        <IconButton
            onClick={toggleDataMenu}
            id={uploadDataBtnId}
            tooltip="Add Data"
            icon={
                <SvgIcon inheritViewBox>
                    <UploadDataBtn />
                </SvgIcon>
            }
            key={'uploadDataIconButton'}
        />
    );

    const projectSettingsIcon = (
        <IconButton
            onClick={handleOpenProjectSettingsDialog}
            id={projectSettingsBtnId}
            tooltip={'Project Settings'}
            icon={
                <SvgIcon inheritViewBox>
                    <SettingsApplicationsOutlinedIcon
                        fontSize="small"
                        color="gradientGrey"
                    />
                </SvgIcon>
            }
            key={'projectSettingsIconButton'}
        />
    );

    const uploadDataMenu = (
        <Menu
            id="upload-data-menu"
            open={dataMenuIsOpen}
            onClose={toggleDataMenu}
            anchorEl={document.getElementById(uploadDataBtnId)}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            keepMounted
            key={'uploadDataMenu'}
        >
            <MenuItem
                id="upload-data-option"
                onClick={() => {
                    handleFileUploadBtnClick();
                    toggleDataMenu();
                }}
                dense
            >
                Upload Data
            </MenuItem>
            <Divider />
            <MenuItem
                id="connect-dept-option"
                onClick={() => {
                    handleConnectDeptClick();
                    toggleDataMenu();
                }}
                disabled={isLoading || !_isNil(selectedProject?.department_id)}
                dense
            >
                Connect IKE Office Department
            </MenuItem>
        </Menu>
    );

    const iconBtns = isLoading ? (
        <Skeleton
            animation="wave"
            width={100}
            height={28}
            style={{ marginLeft: '8px' }}
        />
    ) : (
        [
            createFolder,
            hideFolder,
            uploadData,
            uploadDataMenu,
            projectSettingsIcon,
        ]
    );

    return (
        <React.Fragment>
            <div id="contents-drawer-container" className={Styles.cdContainer}>
                {selectedProject?.department_id && (
                    <Tooltip title="Synced IKE Office dept">
                        <div
                            id="contents-drawer-dept-title"
                            className={Styles.cdDeptName}
                        >
                            {selectedProject?.department_name}
                        </div>
                    </Tooltip>
                )}
                <div
                    id="contents-drawer-icons-container"
                    className={cx(
                        IconBtnStyles.iconBtnGroup,
                        Styles.cdIconBtnContainer
                    )}
                >
                    {iconBtns}
                </div>
                <div
                    id="folders-container"
                    className={Styles.cdFoldersContainer}
                >
                    {foldersHtml}
                </div>
            </div>
            {selectedProject && (
                <React.Fragment>
                    <ManageFolderDialogue
                        selectedFolder={selectedFolder}
                        isOpen={isManageDialogueOpen}
                        onCancel={handleCancelManage}
                        onSubmit={handleSubmitManage}
                    />
                    <ProjectModalDialog
                        selectedFolder={selectedFolder}
                        type={openDialogueType}
                        projectId={selectedProject.project_id}
                        teamId={selectedTeam.team_id}
                        isOpen={!_isNil(openDialogueType)}
                        onCancel={handleDialogueClose}
                        onComplete={handleCompleteProcessEvent}
                    />
                    <DownloadReportsDialogue
                        isOpen={isDownloadFilesOpen}
                        onCancel={handleDialogueClose}
                        onComplete={handleDialogueClose}
                        selectedFolder={selectedFolder}
                    />
                    <JobSettingsDialog
                        isOpen={isJobSettingsOpen}
                        selectedFolder={selectedFolder}
                        onCancel={() => setIsJobSettingsOpen(false)}
                        onComplete={onSaveJobSettings}
                        selectedFolders={[]}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

ContentsDrawer.propTypes = {
    isLoading: PropTypes.bool,
    selectedProject: PropTypes.any,
    onManageProject: PropTypes.func,
};
export default ContentsDrawer;
